(() => {
  'use strict';
  if (window.soilyVslPlayerLoaded) return;
  window.soilyVslPlayerLoaded = true;
  const controllers = new WeakMap();
  const clock = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  function initialize(scope) {
    scope.querySelectorAll('[data-soily-vsl]').forEach(root => {
      if (controllers.has(root)) return;
      const video = root.querySelector('video');
      const controls = root.querySelector('[data-vsl-controls]');
      if (!video || !controls) return;
      const aborter = new AbortController();
      const on = (target, event, handler) => target?.addEventListener(event, handler, {signal: aborter.signal});
      const sound = root.querySelector('[data-vsl-sound-start]');
      const toggle = root.querySelector('[data-vsl-toggle]');
      const mute = root.querySelector('[data-vsl-mute]');
      const captions = root.querySelector('[data-vsl-captions]');
      const seek = root.querySelector('[data-vsl-seek]');
      const time = root.querySelector('[data-vsl-time]');
      const status = root.querySelector('[data-vsl-status]');
      const fullscreen = root.querySelector('[data-vsl-fullscreen]');
      const media = root.querySelector('.soily-vsl-media');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const reducedData = window.matchMedia('(prefers-reduced-data: reduce)');
      const connection = navigator.connection;
      let visibleRatio = 0;
      let intentional = false;
      let intentionalStarted = false;
      let userPaused = false;
      let autoplayBlocked = false;
      let previewSent = false;
      let engagedSent = false;
      let lastTime = null;
      let requestId = 0;
      let action = 'watch_with_sound';
      let observer;
      const milestones = new Set();
      const watchedRanges = [];
      const duration = () => Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 62;
      const position = () => Math.max(0, Math.min(duration(), Number(video.currentTime) || 0));
      const watched = () => watchedRanges.reduce((total, range) => total + range[1] - range[0], 0);
      const noAutoplay = () => reducedMotion.matches || reducedData.matches || connection?.saveData === true;
      const publish = (name, extra = {}) => window.soilyPublishEvent?.(name, {
        video_id: root.dataset.videoId,
        page_path: location.pathname,
        playback_mode: intentional ? 'intentional' : 'muted_preview',
        sound_enabled: !video.muted,
        ...extra
      }) === true;
      const sendEngaged = () => {
        if (intentionalStarted && !engagedSent && publish('soily_vsl_play', {start_action: action})) engagedSent = true;
      };
      function update() {
        toggle.textContent = video.ended ? toggle.dataset.replayLabel : video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
        mute.textContent = video.muted ? mute.dataset.unmuteLabel : mute.dataset.muteLabel;
        if (intentional) sound.textContent = sound.dataset.restartLabel;
        seek.max = String(duration());
        seek.value = String(position());
        seek.disabled = !Number.isFinite(video.duration);
        seek.setAttribute('aria-valuetext', `${clock(position())} / ${clock(duration())}`);
        time.textContent = `${clock(position())} / ${clock(duration())}`;
        const track = video.textTracks[0];
        if (track) captions.setAttribute('aria-pressed', String(track.mode === 'showing'));
      }
      function addWatchedRange(start, end) {
        watchedRanges.push([start, end]);
        watchedRanges.sort((a, b) => a[0] - b[0]);
        for (let i = 1; i < watchedRanges.length;) {
          if (watchedRanges[i][0] <= watchedRanges[i - 1][1] + 0.05) {
            watchedRanges[i - 1][1] = Math.max(watchedRanges[i - 1][1], watchedRanges[i][1]);
            watchedRanges.splice(i, 1);
          } else i++;
        }
      }
      function measure() {
        const now = position();
        if (intentional && !video.seeking && !document.hidden && lastTime !== null) {
          const delta = now - lastTime;
          if (delta > 0 && delta <= 2) addWatchedRange(lastTime, now);
        }
        lastTime = video.paused || video.seeking ? null : now;
        if (!intentionalStarted) return;
        sendEngaged();
        if (!engagedSent) return;
        const progress = watched() / duration() * 100;
        [25, 50, 75].forEach(percent => {
          if (progress >= percent && !milestones.has(percent) && publish('soily_vsl_progress', {percent, progress_basis: 'unique_watched_time', watched_seconds: Math.round(watched())})) milestones.add(percent);
        });
      }
      function pause() {
        requestId++;
        measure();
        video.pause();
        lastTime = null;
        update();
      }
      function play(manual) {
        const currentRequest = ++requestId;
        status.textContent = '';
        status.classList.add('soily-vsl-sr-only');
        video.play().then(() => {
          if (currentRequest !== requestId) return;
          if (!manual && (document.hidden || visibleRatio < 0.6 || noAutoplay() || userPaused)) pause();
          if (manual && !video.paused) {
            intentionalStarted = true;
            lastTime = position();
            sendEngaged();
          }
          update();
        }).catch(() => {
          if (currentRequest !== requestId) return;
          if (!manual) autoplayBlocked = true;
          else {
            status.textContent = status.dataset.errorLabel;
            status.classList.remove('soily-vsl-sr-only');
          }
          update();
        });
      }
      function beginIntentional(withSound, restart, startAction) {
        action = startAction;
        const first = !intentional;
        intentional = true;
        userPaused = false;
        lastTime = null;
        if (restart || first || video.ended) video.currentTime = 0;
        if (withSound) video.muted = false;
        play(true);
        update();
      }
      function considerPreview() {
        if (document.hidden || visibleRatio < 0.25) {
          if (!video.paused) pause();
          return;
        }
        if (!intentional && noAutoplay()) {
          if (!video.paused) pause();
          return;
        }
        if (visibleRatio >= 0.6 && !intentional && !userPaused && !video.ended && !autoplayBlocked && !noAutoplay() && video.paused) {
          video.muted = true;
          play(false);
        }
      }
      on(sound, 'click', () => beginIntentional(true, true, 'watch_with_sound'));
      on(toggle, 'click', () => {
        if (video.paused || video.ended) beginIntentional(false, false, 'resume');
        else { userPaused = true; pause(); }
      });
      on(mute, 'click', () => {
        if (video.muted && !intentional) beginIntentional(true, true, 'unmute');
        else { video.muted = !video.muted; update(); }
      });
      on(captions, 'click', () => {
        const track = video.textTracks[0];
        if (track) track.mode = track.mode === 'showing' ? 'hidden' : 'showing';
        update();
      });
      on(seek, 'input', () => {
        lastTime = null;
        video.currentTime = Math.max(0, Math.min(duration(), Number(seek.value) || 0));
        update();
      });
      on(video, 'playing', () => {
        lastTime = intentional ? position() : null;
        if (intentional) { intentionalStarted = true; sendEngaged(); }
        else if (!previewSent && publish('soily_vsl_preview_start')) previewSent = true;
        update();
      });
      on(video, 'timeupdate', () => { measure(); update(); });
      on(video, 'seeking', () => { lastTime = null; });
      on(video, 'seeked', () => { lastTime = intentional && !video.paused ? position() : null; update(); });
      on(video, 'pause', () => { lastTime = null; update(); });
      on(video, 'loadedmetadata', update);
      on(video, 'volumechange', update);
      on(video, 'ended', () => {
        measure();
        if (intentional && engagedSent && watched() >= duration() * 0.95 && !milestones.has(100) && publish('soily_vsl_progress', {percent: 100, progress_basis: 'unique_watched_time', watched_seconds: Math.round(watched())})) milestones.add(100);
        userPaused = true;
        update();
      });
      on(root.querySelector('track'), 'error', () => {
        captions.disabled = true;
        status.textContent = status.dataset.captionsErrorLabel;
        status.classList.remove('soily-vsl-sr-only');
      });
      on(root.querySelector('[data-vsl-shop]'), 'click', () => publish('soily_vsl_shop_click', {engaged_view: intentionalStarted && watched() > 0, current_time_seconds: Math.round(position())}));
      on(document, 'visibilitychange', considerPreview);
      on(reducedMotion, 'change', considerPreview);
      on(reducedData, 'change', considerPreview);
      on(connection, 'change', considerPreview);
      if (media.requestFullscreen || video.webkitEnterFullscreen) {
        fullscreen.hidden = false;
        on(fullscreen, 'click', () => {
          if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
          else if (media.requestFullscreen) media.requestFullscreen().catch(() => {});
          else video.webkitEnterFullscreen();
        });
        on(document, 'fullscreenchange', () => {
          fullscreen.textContent = document.fullscreenElement ? fullscreen.dataset.exitLabel : fullscreen.dataset.enterLabel;
        });
      }
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          visibleRatio = entries[0].intersectionRatio;
          considerPreview();
        }, {threshold: [0, 0.25, 0.6, 1]});
        observer.observe(video);
      }
      video.muted = true;
      video.controls = false;
      controls.hidden = false;
      root.dataset.playerReady = 'true';
      update();
      controllers.set(root, () => { observer?.disconnect(); aborter.abort(); video.pause(); });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-soily-vsl]').forEach(root => { controllers.get(root)?.(); controllers.delete(root); });
  });
})();
